import { COMPANY_NAME, GATEWAY_URL } from "../env";
import ThreeDSecure from "./3d-secure/ThreeDSecure";
import { strings } from "@securionpay-internal/commons-js";
import Tracker from "../Tracker";
import paymentUserAgent from "./paymentUserAgent";
import ComponentGroup, { IComponentGroup } from "./component/ComponentGroup";
import IntegrationError from "./IntegrationError";
import { PaymentRequestFactory } from "./payment-request/PaymentRequestFactory";
import { PayPalButtonOptions, PayPalHandler } from "./paypal/PayPalHandler";
import { FlowNextActionHandler, HandleChargeNextActionParams } from "./FlowNextActionHandler";
import { ControllerProvider } from "./controller/ControllerProvider";
import Component from "./component/Component";
import Checkout from "./checkout/Checkout";
import { ServerResultHandler } from "./handleServerResult";
import { SecurionPayOptions } from "./types/SecurionPayOptions";
import { TokenRequest } from "./types/request/TokenRequest";
import { DataCollectorService } from "./data-collector/DataCollectorService";
import { MerchantConfigLoader } from "./merchant-config/MerchantConfigLoader";
import { UserActionTokenizeOptions, UserActionType, UserActionUpdateTokenOptions } from "./types/UserAction";
import { PaymentResponse } from "./payment-request/types/PaymentResponse";
import { GooglePaySdkPaymentData } from "./payment-request/types/GooglePaySdkPaymentData";
import ApmModal from "./apm/ApmModal";

import { CreateTokenRequestFactory } from "./CreateTokenRequestFactory";

const generatedJsId = strings.guid();

const validateKey = (key) => {
    if (!key) {
        throw new IntegrationError(`Call ${COMPANY_NAME}() with your Public Key.`);
    }

    if (/^sk_/.test(key)) {
        throw new IntegrationError(`You are using a secret key with ${COMPANY_NAME}.js, instead of the public one.`)
    }

    if (window.location.protocol !== 'https:') {
        if (/^pk_live_/.test(key)) {
            throw new IntegrationError(`Site using ${COMPANY_NAME}.js with live account have to be served through https protocol.`);
        } else {
            console.warn(`Your site is using unsafe protocol. With live account it would be blocked.`);
        }
    }
};


const SecurionPay = (publicKey: string, options: SecurionPayOptions = {}) => {
    validateKey(publicKey)
    const jsId = options.jsId || generatedJsId;
    const tracker = new Tracker(jsId, paymentUserAgent, window.location.href, publicKey, options.merchantId);
    const serverResultHandler = new ServerResultHandler(tracker);
    const controllerProvider = new ControllerProvider(publicKey, options, jsId);
    const defaultController = controllerProvider.create();
    const createTokenRequestFactory = new CreateTokenRequestFactory(controllerProvider);

    const apmModal = new ApmModal(defaultController, publicKey);
    const merchantConfigLoader = new MerchantConfigLoader(defaultController, serverResultHandler);
    const paymentRequestFactory = new PaymentRequestFactory(defaultController, publicKey, options);
    const flowNextActionHandler = new FlowNextActionHandler(defaultController, serverResultHandler, apmModal);
    const threeDSecure = new ThreeDSecure(GATEWAY_URL, publicKey, jsId, defaultController, options);
    const dataCollectorService = new DataCollectorService(options, merchantConfigLoader);
    const payPalHandler = new PayPalHandler(defaultController);

    const defaultComponentGroup = ComponentGroup(defaultController, {});

    return {
        createComponent: (type, options = {}) => {
            return defaultComponentGroup.createComponent(type, options);
        },
        createCheckout: (options = {}) => {
            return new Checkout(defaultController, Object.assign({}, options, { publicKey }));
        },
        createComponentGroup: (options = {}) => {
            return ComponentGroup(controllerProvider.create(), options);
        },
        createToken: (componentOrToken: IComponentGroup | Component | GooglePaySdkPaymentData | PaymentResponse, tokenData: TokenRequest = {}) => {
            const {controller, request} = createTokenRequestFactory.createRequest(componentOrToken, tokenData)
            return dataCollectorService.processTokenCreateRequest(request)
                .then(processedRequest => ({data: processedRequest}) as UserActionTokenizeOptions)
                .then(actionData => controller.action(UserActionType.TOKENIZE, actionData))
                .then(serverResultHandler.handleCallback("custom_form.tokenize", res => ({tokenId: res.id})));
        },
        verifyThreeDSecure: (request) => {
            return threeDSecure.verifyThreeDSecure(request)
                .then(serverResultHandler.handleCallback('three_d_secure.verify', res => ({tokenId: res.id, threeDSecureInfo: res.threeDSecureInfo})));
        },
        handleChargeNextAction: (clientObjectId: string, params: HandleChargeNextActionParams = {}) => {
            return flowNextActionHandler.handleChargeNextAction(clientObjectId, params);
        },
        handlePaymentMethodNextAction: (clientObjectId: string, params: { signal?: AbortSignal } = {}) => {
            return flowNextActionHandler.handlePaymentMethodNextAction(clientObjectId, params);
        },
        updatePaymentMethod: (clientObjectId: string, request: any) => {
            return defaultController.action(UserActionType.PAYMENT_METHOD_UPDATE, { clientObjectId, payload: request });
        },
        collectFraudCheckData: (token: string | { id: string }) => {
            return dataCollectorService.fraudCheckAsTokenUpdate()
                .then(request => {
                    const tokenId = typeof token === "string" ? token : token.id;
                    return {request, tokenId} as UserActionUpdateTokenOptions;
                })
                .then(actionData => defaultController.action(UserActionType.UPDATE_TOKEN, actionData))
                .then(serverResultHandler.handleNoTrackCallback())
        },
        createPaymentRequest: (methodData: PaymentMethodData[], details: PaymentDetailsInit, options?: PaymentOptions) => {
            return paymentRequestFactory.createPaymentRequest(methodData, details, options);
        },
        createPayPalButton: (payPalOptions: PayPalButtonOptions) => {
            return payPalHandler.createButton(payPalOptions);
        }
    }
};

export default SecurionPay;
